import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, ExternalLink, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import VenuePageBuilder from './builder/VenuePageBuilder';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VenuePagesManager = ({ adminToken }) => {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', slug: '' });

  const fetchPages = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/venue-pages`, { params: { admin_token: adminToken } });
      setPages(res.data.pages || []);
    } catch {
      toast.error('Failed to load venue pages');
    }
  }, [adminToken]);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const createPage = async () => {
    if (!createForm.name || !createForm.slug) {
      toast.error('Name and slug are required');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/admin/venue-pages`, createForm, { params: { admin_token: adminToken } });
      toast.success('Venue page created — now build it with sections');
      setShowCreate(false);
      setCreateForm({ name: '', slug: '' });
      await fetchPages();
      setEditing(res.data.page);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create page');
    }
  };

  const duplicatePage = async (page) => {
    try {
      const res = await axios.post(`${API_URL}/admin/venue-pages/${page.id}/duplicate`, null, { params: { admin_token: adminToken } });
      toast.success(`Duplicated as "${res.data.page.name}" (draft)`);
      fetchPages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to duplicate page');
    }
  };

  const deletePage = async (page) => {
    if (!window.confirm(`Delete "${page.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/venue-pages/${page.id}`, { params: { admin_token: adminToken } });
      toast.success('Venue page deleted');
      fetchPages();
    } catch {
      toast.error('Failed to delete page');
    }
  };

  const togglePublish = async (page) => {
    try {
      await axios.put(`${API_URL}/admin/venue-pages/${page.id}`, { published: !page.published }, { params: { admin_token: adminToken } });
      toast.success(page.published ? 'Page unpublished' : 'Page published');
      fetchPages();
    } catch {
      toast.error('Failed to update page');
    }
  };

  if (editing) {
    return (
      <VenuePageBuilder
        page={editing}
        adminToken={adminToken}
        onClose={() => { setEditing(null); fetchPages(); }}
        onSaved={fetchPages}
      />
    );
  }

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="venue-pages-manager">
      <div className="px-6 py-4 border-b border-stone-200 bg-stone-50 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-medium text-stone-900">Venue Pages</h3>
          <p className="text-xs text-stone-500 mt-0.5">Build each venue page from scratch with sections — content, calculators and estimates all live inside each page's editor.</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-stone-900 hover:bg-stone-800" data-testid="create-venue-page-btn">
          <Plus className="w-4 h-4 mr-1" /> New Venue Page
        </Button>
      </div>
      <div className="divide-y divide-stone-100">
        {pages.length === 0 ? (
          <p className="px-6 py-8 text-center text-stone-500">No venue pages yet. Create one to onboard a venue.</p>
        ) : (
          pages.map(page => (
            <div key={page.id} className="px-6 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`venue-page-row-${page.slug}`}>
              <div className="min-w-0">
                <p className="font-medium text-stone-900">{page.name}</p>
                <p className="text-sm text-stone-500">/venues/{page.slug} · {(page.sections || []).length} section{(page.sections || []).length === 1 ? '' : 's'}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-2 py-1 rounded-full ${page.published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {page.published ? 'Published' : 'Draft'}
                </span>
                <Button variant="outline" size="sm" onClick={() => togglePublish(page)} data-testid={`toggle-publish-${page.slug}`}>
                  {page.published ? 'Unpublish' : 'Publish'}
                </Button>
                <a href={`/venues/${page.slug}`} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm" title="Open public page"><ExternalLink className="w-4 h-4" /></Button>
                </a>
                <Button variant="outline" size="sm" title="Duplicate page" onClick={() => duplicatePage(page)} data-testid={`duplicate-venue-page-${page.slug}`}>
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditing(page)} data-testid={`edit-venue-page-${page.slug}`}>
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => deletePage(page)} data-testid={`delete-venue-page-${page.slug}`}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">New Venue Page</DialogTitle>
            <DialogDescription>Create a blank page for a venue, then build it section by section.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input placeholder="Venue name (e.g. Happy Retreats - KAI)" value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') }))} data-testid="create-page-name-input" />
            <Input placeholder="URL slug (e.g. happy-retreats-kai)" value={createForm.slug} onChange={e => setCreateForm(p => ({ ...p, slug: e.target.value }))} data-testid="create-page-slug-input" />
            <Button onClick={createPage} className="w-full bg-stone-900 hover:bg-stone-800" data-testid="create-page-submit-btn">Create Page</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VenuePagesManager;
